import React from 'react';
import { ITableColumns, ITableDataSource } from 'models/table';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { formatDate, leftDays, simpleFormatDate } from 'utils/date';
import {
  IBatchesResponseContent,
  IBatchesSearchForm,
  ILicenceResponseContent,
  ILicencesSearchForm,
  IPrintoutsResponseContent,
  IPrintoutsSearchForm,
  IWeaponLicencePrintBatchHistory,
  IWeaponLicencePrintHistory,
} from 'models/licences';
import { ELicenceStatus, ELicenceStatusIds } from 'pages/LegalEntitiesPage/helpers';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import Icon from 'components/atoms/Icon/Icon';
import Tooltip from 'components/atoms/Tooltip';
import { Box, Link as LinkMui } from '@mui/material';
import { colors } from 'theme/colors';
import { EPermission } from 'models/permissions';
import { Link } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { ECustomFieldType, ICustomFieldByObjectType } from 'models/customField';
import { ILegalEntityCustomField } from 'components/organisms/LegalEntityDetails';
import { getColumnTitleFromCustomField } from 'utils/table';
import { ELicencePrintStatus } from 'constants/LicencePrintStatus';
import { ELicencePrintBatchStatus } from 'constants/LicencePrintBatchStatus';
import { findInDictionary } from 'helpers/dictionary';
import { IDictionary } from 'models/dictionary';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { mapToLegalType } from 'constants/LegalTypes';
import { ILicencePrintoutsHistoryResponse } from 'models/licence';

export const initialSearchFormValues: ILicencesSearchForm = {
  preset: undefined,
  weaponLicenceName: '',
  weaponLicenceNumber: '',
  legalUniqueId: '',
  legalTypeUniqueId: '',
  statusesUniqueIds: [],
  licenceAuthorityUniqueIds: [],
  legalName: '',
  expiryDateFrom: null,
  expiryDateTo: null,
  customFields: [],
};

export const getStatusDataOptions = (t: (key: string) => string) => [
  { label: t('licences.licenceStatus.active'), value: ELicenceStatusIds.ACTIVE },
  { label: t('licences.licenceStatus.expired'), value: ELicenceStatusIds.EXPIRED },
  { label: t('licences.licenceStatus.revoked'), value: ELicenceStatusIds.REVOKED },
  { label: t('licences.licenceStatus.suspended'), value: ELicenceStatusIds.SUSPENDED },
  { label: t('licences.licenceStatus.surrendered'), value: ELicenceStatusIds.SURRENDERED },
  { label: t('licences.licenceStatus.scheduled'), value: ELicenceStatusIds.SCHEDULED },
  { label: t('licences.licenceStatus.cancelled'), value: ELicenceStatusIds.CANCELLED },
];

function getEnumKeyByEnumValue<T extends { [index: string]: string }>(
  myEnum: T,
  enumValue: string[],
): keyof T | null {
  if (!enumValue || !enumValue.length) return null;
  const keys = Object.keys(myEnum).filter((x) => enumValue.includes(myEnum[x]));
  return keys.length > 0 ? keys[0] : null;
}

const mapLicenceIdToString = (licenceId: string[]) =>
  getEnumKeyByEnumValue(ELicenceStatusIds, licenceId);

export const mapToDataSource = (
  licences: ILicenceResponseContent[],
  weaponLicencingAuthority: IDictionary[] | null,
): ITableDataSource[] =>
  licences.map((licence) => ({
    id: licence.uniqueId,
    weaponLicenceName: licence.weaponLicenceName,
    weaponLicenceNumber: licence.weaponLicenceNumber,
    legalType: licence.legalType,
    legalName: licence.legalName,
    applicationStatus: mapLicenceIdToString(licence.statusUniqueId),
    expiryDate: licence.expiryDate,
    startDate: licence.startDate,
    typeToLink:
      licence.legalType === 'INDIVIDUAL'
        ? ELegalEntityTypes.individual
        : ELegalEntityTypes.organisation,
    hasAnyFirearmIllegal: licence.hasAnyIllegalFirearm,
    hasAnyFirearmInactive: licence.hasAnyInactiveFirearm,
    hasAnyLeDataChanged: licence.hasAnyLeDataChanged,
    hasAnyFirearmNotAssignedToLegal: licence.hasAnyFirearmNotAssignedToLegal,
    hasInactiveOwner: licence.hasInactiveOwner,
    printWarning: licence.printWarning,
    renewed: licence.renewed,
    customFields: licence.customFields,
    licenceAuthorityUniqueId: licence.licenceAuthorityUniqueId,
    licencingAuthority: findInDictionary(
      licence.licenceAuthorityUniqueId,
      weaponLicencingAuthority,
    ),
  }));

export const applicationStatusChips = (
  applicationStatus: ELicenceStatus,
  t: (key: string) => string,
  fullWidth: boolean = true,
) => {
  if (applicationStatus === ELicenceStatus.ACTIVE) {
    return (
      <Chips
        text={t('licences.licenceStatus.active')}
        variant={EChipsVariants.success}
        fullWidth={fullWidth}
      />
    );
  }
  if (applicationStatus === ELicenceStatus.EXPIRED) {
    return (
      <Chips
        text={t('licences.licenceStatus.expired')}
        variant={EChipsVariants.warning}
        fullWidth={fullWidth}
      />
    );
  }
  if (applicationStatus === ELicenceStatus.REVOKED) {
    return (
      <Chips
        text={t('licences.licenceStatus.revoked')}
        variant={EChipsVariants.error}
        fullWidth={fullWidth}
      />
    );
  }
  if (applicationStatus === ELicenceStatus.SUSPENDED) {
    return (
      <Chips
        text={t('licences.licenceStatus.suspended')}
        variant={EChipsVariants.error}
        fullWidth={fullWidth}
      />
    );
  }
  if (applicationStatus === ELicenceStatus.SURRENDERED) {
    return (
      <Chips
        text={t('licences.licenceStatus.surrendered')}
        variant={EChipsVariants.error}
        fullWidth={fullWidth}
      />
    );
  }
  if (applicationStatus === ELicenceStatus.SCHEDULED) {
    return (
      <Chips
        text={t('licences.licenceStatus.scheduled')}
        variant={EChipsVariants.primary}
        fullWidth={fullWidth}
      />
    );
  }
  if (applicationStatus === ELicenceStatus.CANCELLED) {
    return (
      <Chips
        text={t('licences.licenceStatus.cancelled')}
        variant={EChipsVariants.inactive}
        fullWidth={fullWidth}
      />
    );
  }
  return '';
};

interface ILicencesTableColumns {
  customFields: ICustomFieldByObjectType[];
  selectedLanguage: string | undefined;
  dateFormat: string;
  t: (key: string) => string;
  leftDaysNotify: number;
}

export const licencesTableColumns = ({
  customFields,
  selectedLanguage,
  dateFormat,
  t,
  leftDaysNotify,
}: ILicencesTableColumns): ITableColumns[] => {
  const customFieldColumns: ITableColumns[] = customFields
    .filter((customField) => customField.visibleInSearchResults)
    .map((customField) => ({
      id: customField.uniqueId,
      key: `customField.${customField.name}`,
      title: getColumnTitleFromCustomField(customField, selectedLanguage),
      isSortable: false,
      width: '160px',
      render: (data, props) => {
        if (props?.customFields?.length) {
          const cf = props.customFields.find(
            (cField: ILegalEntityCustomField) => cField.customFieldName === customField.name,
          );
          if (cf) {
            if (cf.customFieldType === ECustomFieldType.BOOL && t) {
              if (cf.value === null) {
                return '-';
              }
              return cf.value
                ? t('customFields.value.yes.label')
                : t('customFields.value.no.label');
            }
            if (cf.customFieldType === ECustomFieldType.DATE) {
              return formatDate(cf.value, dateFormat);
            }
            return cf.value || '-';
          }
        }
        return '-';
      },
    }));

  return [
    {
      id: '4eaea4ee-d36f-400b-894b-231c81f091cf',
      key: 'info',
      width: '58px',
      isSortable: false,
      title: t('licences.info.label'),
      align: 'center',
      justifyContentHead: 'center',
      render: (_data, props) => {
        const textToTooltip = [];
        if (props.hasInactiveOwner) {
          textToTooltip.push(t('legalEntity.licenceDetails.hasInactiveOwner.text'));
        }
        if (props.applicationStatus === ELicenceStatus.ACTIVE) {
          const leftDaysNumber = props.expiryDate
            ? Number(leftDays(props.expiryDate, t, true))
            : null;
          if (props.hasAnyFirearmIllegal) {
            textToTooltip.push(t('legalEntity.licenceDetails.hasAnyIllegalFirearm.text'));
          }
          if (props.hasAnyFirearmInactive) {
            textToTooltip.push(t('legalEntity.licenceDetails.hasAnyInactiveFirearm.text'));
          }
          if (props.hasAnyLeDataChanged) {
            textToTooltip.push(t('legalEntity.licenceDetails.hasAnyLeDataChanged.text'));
          }
          if (props.printWarning) {
            textToTooltip.push(t(props.printWarning));
          }
          if (
            props.expiryDate &&
            leftDaysNumber !== null &&
            leftDaysNumber <= leftDaysNotify &&
            !props.renewed
          ) {
            textToTooltip.push(
              leftDaysNumber > 0
                ? `${t('legalEntity.licenceDetails.willExpire.text')} ${leftDaysNumber} ${t(
                    'legalEntity.licenceDetails.willExpire.days',
                  )}`
                : t('legalEntity.licenceDetails.willExpireToday.text'),
            );
          }
          if (props.hasAnyFirearmNotAssignedToLegal) {
            textToTooltip.push(
              t('legalEntity.licenceDetails.hasAnyFirearmNotAssignedToLegal.text'),
            );
          }
        }
        if (textToTooltip.length > 0) {
          return (
            <Box sx={{ height: '20px', mt: '4px' }}>
              <Tooltip titleArray={textToTooltip}>
                <Icon type={EIconTypes.infoWarning} fontSize={20} />
              </Tooltip>
            </Box>
          );
        }
        return '';
      },
    },
    {
      id: '4eaea4ee-d36f-400b-895b-231c81f091cf',
      key: 'weaponLicenceName',
      width: '220px',
      title: t('licences.licenceName.label'),
    },
    {
      id: '4eaea4ee-d36f-400b-896b-231c81f091cf',
      key: 'weaponLicenceNumber',
      width: '160px',
      title: t('licences.weaponLicenceNumber.label'),
    },
    {
      id: '4eaea4ee-d36f-410b-895b-231c81f091cf',
      key: 'legalType',
      title: t('licences.legalType.label'),
      width: '94px',
      align: 'center',
      isCenterTableHead: true,
      render: (_data, props) => (
        <Chips
          text={props.legalType.slice(0, 3)}
          variant={
            props.legalType === 'INDIVIDUAL' ? EChipsVariants.primary : EChipsVariants.inactive
          }
        />
      ),
    },
    {
      id: '4eaea4ee-d36f-420b-895b-231c81f091cf',
      key: 'legalName',
      width: '318px',
      title: t('licences.legalName.label'),
    },
    {
      id: '4eaea4ee-d36f-430b-895b-231c81f091cf',
      key: 'licenceAuthorityUniqueId',
      width: '180px',
      title: t('licences.licencingAuthority.label'),
      isSortable: false,
      render: (_data, props) => props.licencingAuthority,
    },
    {
      id: '4eaea4ee-d36f-440b-895b-231c81f091cf',
      key: 'licenceStatus',
      title: t('licences.status.label'),
      isSortable: false,
      isCenterTableHead: true,
      align: 'center',
      width: '140px',
      render: (_data, props) => applicationStatusChips(props.applicationStatus, t),
    },
    {
      id: '4eaea4ee-d36f-450b-895b-231c81f091ce',
      key: 'startDate',
      title: t('licences.startDate.label'),
      width: '110px',
      render: (_data, props) =>
        props.startDate ? formatDate(props.startDate, dateFormat) : '-',
    },
    {
      id: '4eaea4ee-d36f-450b-895b-231c81f091cf',
      key: 'expiryDate',
      title: t('licences.expirationDate.label'),
      width: '110px',
      render: (_data, props) =>
        props.expiryDate ? formatDate(props.expiryDate, dateFormat) : '-',
    },
    ...customFieldColumns,
    {
      id: '4eaea4ee-d36f-500b-895b-231c81f091cf',
      key: '',
      title: t('licences.actions.label'),
      isSortable: false,
      width: '80px',
      align: 'center',
      isCenterTableHead: true,
      render: (_data, props) => (
        <Link
          to={`${ERouteLinks.DetailLicence.replace(':licenceId', props.id)}?type=${
            props.typeToLink
          }`}
        >
          <Button
            variant={EButtonVariants.outlined}
            icon={EIconTypes.view}
            tableButton
            permission={[
              EPermission.VIEW_GLOBAL_LICENCES,
              EPermission.VIEW_RESTRICTED_LICENCES,
              EPermission.VIEW_LICENCES_LE,
            ]}
            permissionOr
          />
        </Link>
      ),
    },
  ];
};

export const mapValuesToLicencesPresetForView = (
  values: { key: string; value: string }[],
  shortDateFormat: string,
) =>
  values.map((item) => {
    switch (item.key) {
      case 'expiryDateFrom':
      case 'expiryDateTo':
        return {
          key: item.key,
          value: simpleFormatDate(new Date(item.value), shortDateFormat),
        };
      default:
        return item;
    }
  });

export const notApplicableFieldHolder = (
  t: (key: string) => string,
  field?: string,
  render: boolean = true,
) => {
  const isEmptyField = (value?: string) =>
    value === undefined || value === null || value.toString().trim() === '';

  if (!render) {
    return (
      <span style={{ fontStyle: 'italic', color: colors.gray500 }}>
        {t('general.notApplicable')}
      </span>
    );
  }
  if (isEmptyField(field)) {
    return '-';
  }

  return field;
};

export const licencePrintStatusVariant = (statusUniqueId: string) => {
  switch (statusUniqueId) {
    case ELicencePrintStatus.PRINTED:
      return EChipsVariants.success;
    case ELicencePrintStatus.WAITING:
      return EChipsVariants.warning;
    case ELicencePrintStatus.BATCHED:
    case ELicencePrintStatus.AVAILABLE:
      return EChipsVariants.primary;
    case ELicencePrintStatus.CANCELLED:
      return EChipsVariants.inactive;
    case ELicencePrintStatus.FAILED:
    case ELicencePrintStatus.FAILED_TO_SEND:
    case ELicencePrintStatus.MISSING:
      return EChipsVariants.error;
    default:
      return EChipsVariants.inactive;
  }
};

export const mapPrintoutsToDataSource = (
  data: IPrintoutsResponseContent[],
  weaponLicencePrintStatus: IDictionary[] | null,
  dateFormat: string,
  printTypes: IDictionary[] | null,
): ITableDataSource[] =>
  data.map((item) => ({
    id: item.uniqueId,
    printoutId: item.id,
    licenceNumber: item.licenceNumber,
    licenceUniqueId: item.licenceUniqueId,
    licenceOwner: item.licenceOwner,
    licenceOwnerUniqueId: item.licenceOwnerUniqueId,
    licenceOwnerType: mapToLegalType(item.licenceOwnerTypeUniqueId),
    licenceAuthority: item.licenceAuthority,
    status: findInDictionary(item.statusUniqueId, weaponLicencePrintStatus),
    statusUniqueId: item.statusUniqueId,
    type: findInDictionary(item.typeUniqueId, printTypes),
    typeUniqueId: item.typeUniqueId,
    createdDate: item.createdDate ? formatDate(item.createdDate, dateFormat) : null,
    printDate: item.printDate ? formatDate(item.printDate, dateFormat) : null,
    batchUniqueId: item.batchUniqueId,
    batchName: item.batchName,
    version: item.version,
    canRetry: false,
  }));

interface IPrintoutsTableColumns {
  t: (key: string) => string;
  handleCancel: (id: string, version: number) => void;
  handleRetry: (id: string) => void;
}

export const printoutsTableColumns = ({
  t,
  handleCancel,
  handleRetry,
}: IPrintoutsTableColumns): ITableColumns[] => {
  return [
    {
      id: '4eaea4ee-d36f-400b-894b-231c81f091cf',
      key: 'id',
      title: t('licences.printoutId.label'),
      width: '120px',
      render: (_data, props) => props.printoutId,
    },
    {
      id: '4eaea4ee-d36f-400b-895b-231c81f091cf',
      key: 'licenceNumber',
      width: '170px',
      title: t('licences.licenceNumber.label'),
      render: (_data, props) => (
        <LinkMui
          href={ERouteLinks.DetailLicence.replace(':licenceId', props.licenceUniqueId)}
          underline="none"
          target="_blank"
          rel="noopener noreferrer"
        >
          {props.licenceNumber}
        </LinkMui>
      ),
    },
    {
      id: '4eaea4ee-d36f-400b-896b-231c81f091cf',
      key: 'licenceOwner',
      title: t('licences.licenceOwnerName.label'),
      render: (_data, props) => (
        <LinkMui
          href={`${ERouteLinks.ViewLegalEntity.replace(':id', props.licenceOwnerUniqueId)}?type=${props.licenceOwnerType}`}
          underline="none"
          target="_blank"
          rel="noopener noreferrer"
        >
          {props.licenceOwner}
        </LinkMui>
      ),
    },
    {
      id: '4eaea4ee-d36f-420b-895b-231c81f091cf',
      key: 'licenceAuthority',
      title: t('licences.licenceAuthority.label'),
    },
    {
      id: '4eaea4ee-d36f-410b-895b-231c81f091cf',
      key: 'status',
      title: t('licences.status.label'),
      width: '120px',
      render: (_data, props) => (
        <Chips
          text={props.status}
          variant={licencePrintStatusVariant(props.statusUniqueId)}
          fullWidth
        />
      ),
    },
    {
      id: '4eaea4ee-d36f-430b-895b-231c81f091cf',
      key: 'typeUniqueId',
      title: t('licences.printType.label'),
      width: '120px',
      render: (_data, props) => props.type,
    },
    {
      id: '4eaea4ee-d36f-450b-895b-231c81f091ce',
      key: 'createdDate',
      title: t('licences.createDate.label'),
      width: '170px',
    },
    {
      id: '4eaea4ee-d36f-450b-895b-231c81f091cf',
      key: 'printDate',
      title: t('licences.printDate.label'),
      width: '170px',
    },
    {
      id: '4eaea4ee-d36f-400b-894b-231c81f091d0',
      key: 'batchName',
      title: t('licences.batchName.label'),
      width: '200px',
      render: (_data, props) => {
        if (props.batchUniqueId) {
          return (
            <LinkMui
              href={ERouteLinks.PrintoutBatchDetails.replace(':id', props.batchUniqueId)}
              underline="none"
              target="_blank"
              rel="noopener noreferrer"
            >
              {props.batchName}
            </LinkMui>
          );
        }
        return '-';
      },
    },
    {
      id: '4eaea4ee-d36f-500b-895b-231c81f091cf',
      key: '',
      title: t('licences.actions.label'),
      isSortable: false,
      width: '120px',
      align: 'center',
      isCenterTableHead: true,
      render: (_data, props) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <Link to={ERouteLinks.PrintoutDetails.replace(':id', props.id)}>
            <Button
              variant={EButtonVariants.outlined}
              icon={EIconTypes.view}
              tableButton
              permission={[
                EPermission.VIEW_GLOBAL_LICENCES,
                EPermission.VIEW_RESTRICTED_LICENCES,
                EPermission.VIEW_LICENCES_LE,
              ]}
              permissionOr
            />
          </Link>
          {props.statusUniqueId === ELicencePrintStatus.WAITING && (
            <Button
              id={`printouts_cancelRequest-button-${props.id}`}
              variant={EButtonVariants.outlined}
              icon={EIconTypes.close}
              tableButton
              onClick={() => handleCancel(props.id, props.version)}
              permission={[EPermission.PRINT_LICENCE]}
            />
          )}
          {props.canRetry && (
            <Button
              variant={EButtonVariants.outlined}
              icon={EIconTypes.renew}
              tableButton
              onClick={() => handleRetry(props.id)}
            />
          )}
        </Box>
      ),
    },
  ];
};
export const mapPrintHistoryToDataSource = (
  data: IWeaponLicencePrintHistory[],
  longDateFormat: string,
): ITableDataSource[] =>
  data.map((item) => ({
    id: `${item.weaponLicenceUniqueId}-${item.printUniqueId}`,
    status: item.status,
    statusUniqueId: item.statusUniqueId,
    description: item.description,
    createdDate: item.createdDate ? formatDate(item.createdDate, longDateFormat) : null,
  }));

export const printoutsHistoryTableColumns = ({
  t,
}: {
  t: (key: string) => string;
}): ITableColumns[] => {
  return [
    {
      id: '4eaea4ee-d36f-400b-894b-231c81f091cf',
      key: 'createdDate',
      title: 'licence.applicationProcess.dateTime.label',
      width: '200px',
    },
    {
      id: '4eaea4ee-d36f-410b-895b-231c81f091cf',
      key: 'status',
      title: 'licence.applicationProcess.status.label',
      width: '160px',
      render: (_data, props) => (
        <Chips
          text={props.status}
          variant={licencePrintStatusVariant(props.statusUniqueId)}
          fullWidth
        />
      ),
    },
    {
      id: '4eaea4ee-d36f-450b-895b-231c81f091ce',
      key: 'description',
      title: 'licence.applicationProcess.priceDescription.label',
      render: (_data, props) => t(props.description),
    },
  ];
};

export const batchPrintoutsTableColumns = ({
  t,
}: {
  t: (key: string) => string;
}): ITableColumns[] => [
  {
    id: '2b7d4e2a-0b1a-4c2e-9c1a-0a1b2c3d4e5f',
    key: 'id',
    title: t('licences.printoutId.label'),
    render: (_data, props) => props.printoutId,
  },
  {
    id: '3c8e5f3b-1c2b-5d3f-0d2b-1b2c3d4e5f60',
    key: 'licenceNumber',
    title: t('licences.licenceNumber.label'),
    render: (_data, props) => (
      <LinkMui
        href={ERouteLinks.DetailLicence.replace(':licenceId', props.licenceUniqueId)}
        underline="none"
        target="_blank"
        rel="noopener noreferrer"
      >
        {props.licenceNumber}
      </LinkMui>
    ),
  },
  {
    id: '4d9f6a4c-2d3c-6e40-1e3c-2c3d4e5f6071',
    key: 'licenceOwner',
    title: t('licences.licenceOwnerName.label'),
    render: (_data, props) => (
      <LinkMui
        href={`${ERouteLinks.ViewLegalEntity.replace(':id', props.licenceOwnerUniqueId)}?type=${props.licenceOwnerType}`}
        underline="none"
        target="_blank"
        rel="noopener noreferrer"
      >
        {props.licenceOwner}
      </LinkMui>
    ),
  },
  {
    id: '6fb18c6e-4f5e-8062-3050-4e5f60718293',
    key: 'status',
    title: t('licences.status.label'),
    isCenterTableHead: true,
    justifyContentHead: 'center',
    render: (_data, props) => (
      <Chips
        text={props.status}
        variant={licencePrintStatusVariant(props.statusUniqueId)}
        fullWidth
      />
    ),
  },
  {
    id: '70c29d7f-506f-9173-4161-5f6071829304',
    key: 'createdDate',
    title: t('licences.createDate.label'),
  },
  {
    id: '81d3ae80-6170-a284-5272-60718293a415',
    key: 'printDate',
    title: t('licences.printDate.label'),
  },
  {
    id: '4eaea4ee-d36f-500b-895b-231c81f091cf',
    key: '',
    title: t('licences.actions.label'),
    isSortable: false,
    width: '120px',
    align: 'center',
    isCenterTableHead: true,
    render: (_data, props) => (
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
        <Link to={ERouteLinks.PrintoutDetails.replace(':id', props.id)}>
          <Button
            variant={EButtonVariants.outlined}
            icon={EIconTypes.view}
            tableButton
            permission={[
              EPermission.VIEW_GLOBAL_LICENCES,
              EPermission.VIEW_RESTRICTED_LICENCES,
              EPermission.VIEW_LICENCES_LE,
            ]}
            permissionOr
          />
        </Link>
      </Box>
    ),
  },
];

export const mapBatchHistoryToDataSource = (
  data: IWeaponLicencePrintBatchHistory[],
  weaponLicencePrintBatchStatus: IDictionary[] | null,
  dateFormat: string,
): ITableDataSource[] =>
  data.map((item) => ({
    id: `${item.uniqueId}`,
    createdDate: formatDate(item.createdDate, dateFormat),
    status: findInDictionary(item.statusUniqueId, weaponLicencePrintBatchStatus),
    statusUniqueId: item.statusUniqueId,
    description: item.description,
    annotation: item.annotation,
  }));

interface IBatchHistoryTableColumns {
  t: (key: string) => string;
}

export const batchHistoryTableColumns = ({
  t,
}: IBatchHistoryTableColumns): ITableColumns[] => [
  {
    id: '92e4bf91-7281-b395-6383-718293a415b5',
    key: 'createdDate',
    title: t('licences.batchHistory.dateTime.column'),
    width: '220px',
    isSortable: false,
  },
  {
    id: 'a3f5c0a2-8392-c4a6-7494-8293a415b5c6',
    key: 'status',
    title: t('licences.batchStatus.label'),
    width: '160px',
    isSortable: false,
    isCenterTableHead: true,
    justifyContentHead: 'center',
    render: (_data, props) => (
      <Chips
        text={props.status}
        variant={batchStatusVariant(props.statusUniqueId)}
        fullWidth
      />
    ),
  },
  {
    id: 'b406d1b3-94a3-d5b7-85a5-93a415b5c6d7',
    key: 'description',
    title: t('licences.batchHistory.description.column'),
    isSortable: false,
    render: (_data, props) => emptyFieldHolder(t(props.description)),
  },
  {
    id: 'b406d1b3-94a3-d5b7-85a5-93a415b5c6d9',
    key: 'annotation',
    title: t('licences.batchHistory.annotation.column'),
    isSortable: false,
  },
];

export const mapBatchesToDataSource = (
  data: IBatchesResponseContent[],
  dateFormat: string,
  weaponLicencingAuthority: IDictionary[] | null,
  weaponLicencePrintBatchStatus: IDictionary[] | null,
): ITableDataSource[] =>
  data.map((item) => ({
    id: item.uniqueId,
    name: item.name,
    licencingAuthority:
      findInDictionary(item.licencingAuthorityUniqueId, weaponLicencingAuthority) || '-',
    statusUniqueId: item.statusUniqueId,
    status:
      findInDictionary(item.statusUniqueId, weaponLicencePrintBatchStatus) ||
      item.statusUniqueId,
    createdDate: item.createdDate ? formatDate(item.createdDate, dateFormat) : '-',
    processedDate: item.processedDate ? formatDate(item.processedDate, dateFormat) : '-',
  }));

export const batchStatusVariant = (statusUniqueId?: string): EChipsVariants => {
  switch (statusUniqueId) {
    case ELicencePrintBatchStatus.PROCESSED:
      return EChipsVariants.success;
    case ELicencePrintBatchStatus.SENT:
      return EChipsVariants.warning;
    case ELicencePrintBatchStatus.FAILED_TO_SEND:
    case ELicencePrintBatchStatus.FAILED_RETRY_NEEDED:
    case ELicencePrintBatchStatus.REJECTED:
      return EChipsVariants.error;
    case ELicencePrintBatchStatus.CREATED:
      return EChipsVariants.primary;
    default:
      return EChipsVariants.inactive;
  }
};

export const batchesTableColumns = (
  t: (key: string) => string,
  onClickDetails: (id: string) => void,
): ITableColumns[] => [
  {
    id: '5eaea4ee-d36f-400b-895b-231c81f091cf',
    key: 'name',
    title: t('licences.batchName.label'),
    width: '300px',
  },
  {
    id: '5eaea4ee-d36f-410b-895b-231c81f091cf',
    key: 'licencingAuthority',
    title: t('licences.licenceAuthority.label'),
    width: '180px',
    render: (_data, props) => props.licencingAuthority || '-',
  },
  {
    id: '5eaea4ee-d36f-420b-895b-231c81f091cf',
    key: 'status',
    isSortable: true,
    title: t('licences.batchStatus.label'),
    isCenterTableHead: true,
    width: '160px',
    render: (_data, props) => (
      <Chips
        text={props.status}
        variant={batchStatusVariant(props.statusUniqueId)}
        fullWidth
      />
    ),
  },
  {
    id: '5eaea4ee-d36f-430b-895b-231c81f091cf',
    key: 'createdDate',
    title: t('licences.createDate.label'),
    width: '170px',
  },
  {
    id: '5eaea4ee-d36f-440b-895b-231c81f091cf',
    key: 'processedDate',
    title: t('licences.manifestDate.label'),
    width: '170px',
  },
  {
    id: '1eaea4ee-d36f-440b-895b-231c81f091cf',
    key: 'actions',
    isSortable: false,
    title: t('licences.actions.label'),
    width: '60px',
    align: 'center',
    isCenterTableHead: true,
    render: (_data, props) => (
      <Tooltip titleArray={[t('licences.details.label')]}>
        <Button
          id={`batches__details-btn--${props.id}`}
          variant={EButtonVariants.outlined}
          icon={EIconTypes.view}
          tableButton
          onClick={() => onClickDetails(props.id)}
        />
      </Tooltip>
    ),
  },
];

export const initialBatchesSearchForm: IBatchesSearchForm = {
  search: '',
  licenceAuthorityUniqueIds: [],
  statusUniqueIds: [],
  requestDateFrom: '',
  requestDateTo: '',
};

export enum EBatcheslabels {
  licenceAuthorityUniqueIds = 'licences.licenceAuthority.label',
  statusUniqueIds = 'licences.batchStatus.label',
  requestDateFrom = 'licences.createDateFrom.label',
  requestDateTo = 'licences.createDateTo.label',
}

export const initialPrintoutsSearchForm: IPrintoutsSearchForm = {
  statusUniqueIds: [],
  requestDateFrom: '',
  requestDateTo: '',
  licenceOwner: '',
  licenceAuthorityUniqueIds: [],
  search: '',
};

export enum EPrintoutslabels {
  modulePrintoutsName = 'module.printouts.name',
  printoutId = 'licences.printoutId.label',
  licenceOwner = 'licences.licenceOwnerName.label',
  licenceAuthorityUniqueIds = 'licences.licenceAuthority.label',
  statusUniqueIds = 'licences.status.label',
  createDate = 'licences.createDate.label',
  printDate = 'licences.printDate.label',
  retry = 'licences.retry.label',
  cancel = 'licences.cancel.label',
  requestDateFrom = 'licences.createDateFrom.label',
  requestDateTo = 'licences.createDateTo.label',
}
